import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { Badge, Card, Divider, Flex, Grid, Group, Image, Space, Text, Title } from '@mantine/core';
import { AutomatedAuditLoader, Button, Textarea } from '@common/components';
import { Bar } from 'react-chartjs-2';
import { SemiCircleProgress } from 'src/components';
import { languageLevelChecker, useAppDispatch, useAppSelector } from 'src/store';
import { shallowEqual } from 'react-redux';


const LanguageLevelChecker: React.FC = () => {
    const [text, setText] = useState('');
    const [testResultat, setTestResultat] = useState('');
    const [level, setLevel] = useState<Level>();
    const [stats, setStats] = useState<{ wordCount: number; sentenceCount: number; averageWordLength: number; averageSentenceLength: number } | null>(null);
    const dispatch = useAppDispatch();
    const { languageLevelCheckerData, loadinglanguageLevelChecker } = useAppSelector((state) => state.tools, shallowEqual);
    const [fleschScore, setFleschScore] = useState<any>(null);
    

    useEffect(() => {
      if(!languageLevelCheckerData){return;}
      setTestResultat(languageLevelCheckerData);
      extractScore(languageLevelCheckerData)
    }, [languageLevelCheckerData])

    useEffect(() => {
      if (!fleschScore) {
        return;
      }


      const words = text.split(/\s+/).filter((word) => word.length > 0);
      const sentences = text.split(/[.!?]+/).filter((sentence) => sentence.length > 0);
      const wordCount = words.length;
      const sentenceCount = sentences.length || 1; // To avoid division by zero
      const averageWordLength = words.reduce((sum, word) => sum + word.length, 0) / wordCount;
      const averageSentenceLength = wordCount / sentenceCount;

      // // Set the statistics
      setStats({ wordCount, sentenceCount, averageWordLength, averageSentenceLength });

      // // Determine the level based on average sentence length and word length
      let estimatedLevel: Level = 'A1';
      if (fleschScore >= 90) {
        estimatedLevel ='A1';
      } else if (fleschScore >= 80) {
        estimatedLevel ='A2';
      } else if (fleschScore >= 70) {
        estimatedLevel ='B1';
      } else if (fleschScore >= 60) {
        estimatedLevel ='B2';
      } else if (fleschScore >= 50) {
        estimatedLevel ='C1';
      } else {
        estimatedLevel ='C2';
      }

      setLevel(estimatedLevel);

      if( mainContentRef?.current ) {
          mainContentRef?.current?.focus();
      }

    
    }, [fleschScore])
    
    
    const mainContentRef = useRef<HTMLDivElement>(null)
    const checkLanguageLevel = () => {
        if (!text) return;

            dispatch(languageLevelChecker({textInput: text }));

    };

    const levelMap = {
      'A1': 90,  // Text is easiest to read
      'A2': 80,
      'B1': 70,
      'B2': 60,
      'C1': 50,
      'C2': 40  // Text is more complex to read
    };
    

    const extractScore = (text:any) => {
      const scorePattern = /(\d+\.\d+)/; // Regular expression to find a decimal number
      const match = text.match(scorePattern);
      if (match) {
        setFleschScore(match[1]); // match[1] contains the first captured group
      } else {
        setFleschScore('No score found');
      }
    };
  

    const scorePercentage = levelMap['A1']
  

    
    
    

    return (
        <div style={{ backgroundColor: '#F5F7FA', width: 'calc(100% - 300px)' }}>
            <div className={styles.container_bg}>
                <Flex justify={'space-between'} direction="column">
                    <Title order={1} color="#264547" className={styles.heading}>
                        Language Level Checker
                    </Title>
                </Flex>
                <Space h="md" />
                <Divider />
                <Space h="md" />
            </div>

            <Grid mr={24} >
                <Grid.Col sm={12} md={7}  span={7}>
                <div className={styles.container}>
                {/* <h2 className={styles.heading}>Language Level Checker</h2> */}
                <Textarea
                    label=''
                    // value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Enter text to check language level"
                    autoSize
                    minRows={8}
                    // className={styles.textarea}
                    
                />
                <Space h={"sm"} />
                <Button onClick={checkLanguageLevel} 
                        disabled={!text || loadinglanguageLevelChecker}
                >
                    {
                        loadinglanguageLevelChecker ? ( <span >
                            Checking Text ...
                            <AutomatedAuditLoader />
                            </span>
                        ) :
                        'Check Level'
                    }
                </Button>


                
                <div ref={mainContentRef} className={styles.result}>
                  <span>{testResultat}</span>
                </div>
                <div  className={styles.result}>
                    <strong>Estimated Level:</strong> <span className={styles.resultText}>{level}</span>
                </div>
               
               
                <SemiCircleProgress
                    percentage={ level ?  levelMap[level] : 0}
                    showPercentValue={true}
                    diameter={200}
                    strokeWidth={9}
                />
                <div className={styles.stats}>
                    <div>
                      <h3>Text Analysis</h3>
                      <p>Word Count: {stats?.wordCount ?? "0" }</p>
                      <p>Sentence Count: {stats?.sentenceCount ?? "0" }</p>
                      <p>Average Word Length: {stats?.averageWordLength.toFixed(2) ?? "0" } characters</p>
                      <p>Average Sentence Length: {stats?.averageSentenceLength.toFixed(2) ?? "0" } words</p>
                    </div>
                    <div >
                        <Flex className={styles.textLevel} gap="sm" align="center">
                          <p>Level: 90% - 100% - <strong>A1 (Beginner)</strong></p>
                        </Flex>
                        <Flex className={styles.textLevel} gap="sm" align="center">
                          <p>Level: 80% - 90% - <strong>A2 (Elementary)</strong></p>
                        </Flex>
                        <Flex className={styles.textLevel} gap="sm" align="center">
                          <p>Level: 70% - 80% - <strong>B1 (Intermediate)</strong></p>
                        </Flex>
                        <Flex className={styles.textLevel} gap="sm" align="center">
                          <p>Level: 60% - 70% - <strong>B2 (Upper Intermediate)</strong></p>
                        </Flex>
                        <Flex className={styles.textLevel} gap="sm" align="center">
                          <p>Level: 50% - 60% - <strong>C1 (Advanced)</strong></p>
                        </Flex>
                        <Flex className={styles.textLevel} gap="sm" align="center">
                          <p>Level: 40% - 50% - <strong>C2 (Proficient)</strong></p>
                        </Flex>
                    </div> 

                </div>
                

                {/* {level && (
                    <div className={styles.progressContainer}>
                        <Text fw={"bolder"} size={"md"}>Progress:</Text>
                        <div className={`${styles.progressBar} ${styles[level.toLowerCase()]}`} />
                    </div>
                )} */}
            </div>
                </Grid.Col>
                <Grid.Col sm={12} md={5}   span={5} p={"md"}>
                    <Card shadow={"sm"} p={"sm"}>
                        <Title size={"20px"} color="#264547" order={2}>Estimated Level</Title>
                        <p><span style={{ color: "#0000CD" }}>A1 (Beginner):</span> Can understand and use basic phrases and expressions for everyday situations.</p>
                        <p>Can introduce themselves and answer questions about personal details like where they live or their hobbies.</p>
                        <p>Can interact simply as long as the other person speaks slowly and clearly.</p>
                        <p><span style={{ color: "#0000CD" }}>A2 (Elementary):</span> Can understand frequently used expressions related to areas of immediate relevance (e.g., shopping, family, employment).</p>
                        <p>Can communicate in simple tasks requiring a direct exchange of information on familiar topics.</p>
                        <p>Can describe aspects of their background and environment in simple terms.</p>

                        <p><span style={{ color: "#0000CD" }}>B1 (Intermediate):</span> Can understand the main points of clear, standard communication on familiar topics (work, school, leisure).</p>
                        <p>Can deal with most situations likely to arise while traveling in areas where the language is spoken.</p>
                        <p>Can produce simple, connected text on topics of personal interest.</p>
                        <p>Can describe experiences, events, dreams, and ambitions, and give brief explanations or reasons.</p>

                        <p><span style={{ color: "#0000CD" }}>B2 (Upper Intermediate):</span> Can understand the main ideas of complex texts on both concrete and abstract topics.</p>
                        <p>Can interact with a degree of fluency and spontaneity that makes regular interaction with native speakers possible without strain.</p>
                        <p>Can produce clear, detailed text on various topics and explain viewpoints on topical issues.</p>

                        <p><span style={{ color: "#0000CD" }}>C1 (Advanced):</span> Can understand a wide range of demanding, longer texts and recognize implicit meaning.</p>
                        <p>Can express themselves fluently and spontaneously without searching for expressions.</p>
                        <p>Can use the language flexibly and effectively for social, academic, and professional purposes.</p>

                        <p><span style={{ color: "#0000CD" }}>C2 (Proficient):</span> Can understand virtually everything heard or read with ease.</p>
                        <p>Can summarize information from different spoken and written sources, reconstructing arguments coherently.</p>
                        <p>Can express themselves spontaneously, fluently, and precisely, even in complex situations.</p>
                    </Card>
                </Grid.Col>
            </Grid>
          
        </div>
    );
};

export default LanguageLevelChecker;

type Level = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2'
const LevelScoreChart = ({ scoreLevel } : { scoreLevel : Level }) => {
    // Map scoreLevel to numeric value for better chart handling
    const levelMap = {
      A1: 10,
      A2: 10,
      B1: 30,
      B2: 50,
      C1: 70,
      C2: 90
    };
  
    const scorePercentage = levelMap[scoreLevel] || 0;
  
    const data = {
      labels: ['A1', 'B1', 'B2', 'C1', 'C2'],  // Labels for the proficiency levels
      datasets: [
        {
          label: 'Proficiency Levels',
          data: [10, 30, 50, 70, 90], // Percentage ranges for each level
          backgroundColor: [
            '#e6f7ff', // A1
            '#cce0ff', // B1
            '#99c2ff', // B2
            '#66b3ff', // C1
            '#3399ff'  // C2
          ],
          borderColor: '#000',  // Border color for bars
          borderWidth: 1,
        },
        {
          label: 'User Score',
          data: [scorePercentage, scorePercentage, scorePercentage, scorePercentage, scorePercentage],
          backgroundColor: 'rgba(255, 99, 132, 0.8)',  // Color for the user's score
          borderColor: '#ff6384',
          borderWidth: 2,
        }
      ]
    };
  
    const options = {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: 'Language Proficiency Levels',
          font: {
            size: 18
          }
        },
        tooltip: {
          callbacks: {
            label: (context:any) => {
              return `Level: ${context.label} | ${context.raw}%`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            stepSize: 10,
          }
        }
      }
    };
  
    return (
      <div>
        <h3>Proficiency Level Score</h3>
        <Bar data={data} options={options} />
      </div>
    );
  };
  